import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { fmt } from "../lib/format";
import { cn } from "@/lib/utils";

interface PaginatorProps {
    page: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
    onChange: (page: number) => void;
    loading?: boolean;
}

export function Paginator({
    page,
    totalPages,
    totalItems,
    pageSize,
    onChange,
    loading,
}: PaginatorProps) {
    const [jumpVal, setJumpVal] = useState("");
    if (totalPages <= 1) return null;

    const safePage = Math.min(Math.max(1, page), totalPages);
    const fromItem = (safePage - 1) * pageSize + 1;
    const toItem = Math.min(safePage * pageSize, totalItems);

    const buildPages = (): (number | "…")[] => {
        const pages: (number | "…")[] = [];
        const push = (v: number | "…") => {
            if (pages[pages.length - 1] !== v) pages.push(v);
        };
        push(1);
        if (safePage > 3) push("…");
        for (
            let i = Math.max(2, safePage - 1);
            i <= Math.min(totalPages - 1, safePage + 1);
            i++
        )
            push(i);
        if (safePage < totalPages - 2) push("…");
        if (totalPages > 1) push(totalPages);
        return pages;
    };

    const onJump = (e: React.FormEvent) => {
        e.preventDefault();
        const v = parseInt(jumpVal, 10);
        if (v >= 1 && v <= totalPages) {
            onChange(v);
            setJumpVal("");
        }
    };

    return (
        <div className="bg-white border border-gray-100 rounded-xl px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-gray-500">
                Mostrando{" "}
                <span className="font-semibold text-gray-900 font-mono">
                    {fmt(fromItem)}–{fmt(toItem)}
                </span>{" "}
                de{" "}
                <span className="font-semibold text-gray-900 font-mono">
                    {fmt(totalItems)}
                </span>
                <span className="text-gray-400 ml-1">
                    · página {safePage}/{totalPages}
                </span>
            </div>

            <div className="flex items-center gap-1">
                <button
                    onClick={() => onChange(safePage - 1)}
                    disabled={safePage === 1 || loading}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 rounded-md border border-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                    <ChevronLeft className="w-3.5 h-3.5" />
                    Anterior
                </button>

                <div className="flex items-center gap-0.5 mx-1">
                    {buildPages().map((p, i) =>
                        p === "…" ? (
                            <span key={i} className="px-1.5 text-gray-400 text-xs">
                                …
                            </span>
                        ) : (
                            <button
                                key={i}
                                onClick={() => onChange(p)}
                                disabled={loading}
                                className={cn(
                                    "min-w-[28px] h-7 px-2 text-xs font-semibold rounded-md transition-colors",
                                    p === safePage
                                        ? "bg-blue-600 text-white"
                                        : "text-gray-700 hover:bg-gray-100"
                                )}
                            >
                                {p}
                            </button>
                        )
                    )}
                </div>

                <button
                    onClick={() => onChange(safePage + 1)}
                    disabled={safePage === totalPages || loading}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 rounded-md border border-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                    Próxima
                    <ChevronRight className="w-3.5 h-3.5" />
                </button>
            </div>

            <form onSubmit={onJump} className="flex items-center gap-1.5">
                <span className="text-[11px] text-gray-500">Ir para</span>
                <input
                    type="number"
                    min={1}
                    max={totalPages}
                    value={jumpVal}
                    onChange={(e) => setJumpVal(e.target.value)}
                    placeholder={String(safePage)}
                    className="w-14 text-xs px-2 py-1 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-400 text-center font-mono"
                />
            </form>
        </div>
    );
}
